import classNames from "classnames";
import type { TaskGroup } from "../../lib/viewEngine";
import { PRIO } from "../../lib/design";
import { formatDay, isOverdue } from "../../lib/date";
import { Avatar } from "../Avatar";
import { QuickAdd } from "../QuickAdd";
import { useDrag } from "../../hooks/useDrag";
import type { LayoutProps } from "./ListLayout";

interface Props extends Omit<LayoutProps, "onToggleDone"> {
  /** Ordre persisté des colonnes (clés de groupe) — front-only dans la vue. */
  columnOrder?: string[];
  onReorderColumns?: (order: string[]) => void;
}

/** Layout Board — colonnes = valeurs du champ de groupage, cartes draggables,
 * colonnes réordonnables (drag d'en-tête), composer par colonne. */
export function BoardLayout({
  groups,
  selectedTaskId,
  onSelect,
  hiddenProps,
  canDrop,
  onDropTask,
  composerFor,
  composerBusy,
  columnOrder,
  onReorderColumns,
}: Props) {
  const cards = useDrag();
  const cols = useDrag();
  const show = (p: string) => !hiddenProps?.includes(p);

  const ordered = columnOrder?.length
    ? [...groups].sort((a, b) => {
        const ia = columnOrder.indexOf(a.key);
        const ib = columnOrder.indexOf(b.key);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
    : groups;

  const reorder = (fromKey: string, toKey: string) => {
    if (!onReorderColumns || fromKey === toKey) return;
    const order = ordered.map((g) => g.key);
    const from = order.indexOf(fromKey);
    const to = order.indexOf(toKey);
    if (from === -1 || to === -1) return;
    order.splice(to, 0, ...order.splice(from, 1));
    onReorderColumns(order);
  };

  const renderCard = (t: TaskGroup["items"][number]) => {
    const done = t.status === "done";
    return (
      <div
        key={t.id}
        className={classNames("board-card", {
          selected: t.id === selectedTaskId,
        })}
        {...(t.replicated ? {} : cards.dragProps(t.id))}
        onClick={() => onSelect(t.id === selectedTaskId ? null : t.id)}
        style={
          t.id === selectedTaskId
            ? {
                borderColor: "var(--accent-border)",
                background: "var(--accent-wash)",
              }
            : undefined
        }
      >
        <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>
          {t.replicated && (
            <span style={{ color: "var(--violet)" }} title="Work item PM">
              ◆{" "}
            </span>
          )}
          {t.title}
        </div>
        <div className="board-card-meta">
          {show("priority") && (
            <span
              className="chip"
              style={{
                color: PRIO[t.priority].color,
                background: PRIO[t.priority].bg,
              }}
            >
              {PRIO[t.priority].label}
            </span>
          )}
          {show("list") && t.listName && (
            <span className="tag">{t.listName}</span>
          )}
          {show("due") && t.dueKey && (
            <span
              className={classNames("task-due", {
                overdue: !done && isOverdue(t.dueKey),
              })}
            >
              ◷ {formatDay(t.dueKey)}
            </span>
          )}
          {show("assignees") && t.assignees && t.assignees.length > 0 && (
            <span className="avatar-stack" style={{ marginLeft: "auto" }}>
              {t.assignees.slice(0, 3).map((a) => (
                <Avatar key={a.ref} name={a.name} size={18} />
              ))}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="board">
      {ordered.map((g) => {
        const droppable = canDrop(g.key);
        const onAdd = composerFor(g.key);
        return (
          <div
            key={g.key}
            className={classNames("board-col", {
              "drop-over": cards.overKey === g.key || cols.overKey === g.key,
              "drop-forbidden": cards.dragId !== null && !droppable,
            })}
            {...cards.dropProps(
              g.key,
              (id) => onDropTask(id, g.key),
              droppable,
            )}
          >
            <div
              className="group-title board-col-head"
              {...(onReorderColumns ? cols.dragProps(g.key) : {})}
              {...cols.dropProps(
                g.key,
                (from) => reorder(from, g.key),
                !!onReorderColumns,
              )}
              title={onReorderColumns ? "Glisser pour réordonner" : undefined}
            >
              {g.color && (
                <span className="group-dot" style={{ background: g.color }} />
              )}
              {g.label}
              <span style={{ fontWeight: 500 }}>{g.items.length}</span>
            </div>
            {g.items.map(renderCard)}
            {g.items.length === 0 && (
              <div className="empty-state" style={{ padding: "6px 4px" }}>
                —
              </div>
            )}
            {onAdd && (
              <div className="group-composer">
                <QuickAdd
                  placeholder="Ajouter…"
                  onAdd={onAdd}
                  busy={composerBusy}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
